import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  X, ChevronRight, ChevronLeft, CheckCircle2, Sparkles,
  Target, Zap, BookOpen, Brain, AlertTriangle, BarChart3,
  Newspaper, PenTool, Calendar, RefreshCw
} from 'lucide-react';

const TOUR_STORAGE_KEY = 'clarias_guided_tour_completed';
const TOUR_STEP_KEY = 'clarias_guided_tour_step';

// Tour steps configuration - 11 features
const tourSteps = [
  {
    id: 'selection-probability',
    title: 'Selection Probability',
    description: 'Track your realistic chances of clearing UPSC based on your preparation progress, mock scores, and consistency.',
    icon: Target,
    iconBg: 'bg-gradient-to-br from-violet-500 to-purple-600',
    route: '/dashboard',
    highlight: 'Your selection probability changes as per your performance!',
    color: 'violet'
  },
  {
    id: 'daily-challenge',
    title: 'Daily MCQ Challenge',
    description: 'Take 10 UPSC-standard MCQs daily. Build consistency and track your streak to stay exam-ready.',
    icon: Zap,
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
    route: '/daily-challenge',
    highlight: 'Complete daily to maintain your streak!',
    color: 'amber'
  },
  {
    id: 'mcq-practice',
    title: 'MCQ Practice',
    description: 'Practice unlimited MCQs on any sub-topic. Fresh questions generated based on UPSC patterns.',
    icon: BookOpen,
    iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    route: '/prelims',
    highlight: 'Choose any topic and get instant practice questions!',
    color: 'blue'
  },
  {
    id: 'csat-suite',
    title: 'CSAT Suite',
    description: 'Complete CSAT preparation with comprehension, logical reasoning, math, and decision-making practice.',
    icon: Brain,
    iconBg: 'bg-gradient-to-br from-cyan-500 to-teal-600',
    route: '/csat',
    highlight: 'CSAT is qualifying - don\'t leave it to chance!',
    color: 'cyan'
  },
  {
    id: 'mistake-tracker',
    title: 'Mistake Tracker',
    description: 'All your wrong answers from MCQs are automatically saved here. Review and conquer your weak spots.',
    icon: AlertTriangle,
    iconBg: 'bg-gradient-to-br from-red-500 to-rose-600',
    route: '/mistake-tracker',
    highlight: 'Learn from every mistake - they\'re your best teachers!',
    color: 'red'
  },
  {
    id: 'pyq-analyzer',
    title: 'PYQ Analyzer',
    description: 'Analyze 30 years of previous year questions. Discover patterns, trends, and high-frequency topics.',
    icon: BarChart3,
    iconBg: 'bg-gradient-to-br from-emerald-500 to-green-600',
    route: '/pyq',
    highlight: '30 years of PYQ data at your fingertips!',
    color: 'emerald'
  },
  {
    id: 'newspaper-mcq-converter',
    title: 'Newspaper to MCQ Converter',
    description: 'Convert any newspaper article into UPSC-style MCQs instantly. Paste an article and get practice questions.',
    icon: RefreshCw,
    iconBg: 'bg-gradient-to-br from-orange-500 to-amber-600',
    route: '/prelims/newspaper-converter',
    highlight: 'Turn any news article into practice MCQs!',
    color: 'orange'
  },
  {
    id: 'newspaper-mcq',
    title: 'Newspaper MCQs',
    description: 'Current affairs MCQs generated from daily news. Stay updated with exam-relevant questions.',
    icon: Newspaper,
    iconBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
    route: '/current-affairs-prelims',
    highlight: 'Fresh MCQs from today\'s news!',
    color: 'pink'
  },
  {
    id: 'answer-writing',
    title: 'Answer Writing Practice',
    description: 'Write Mains answers and get expert-style evaluation. Practice for GS1, GS2, GS3, GS4, and Essay papers.',
    icon: PenTool,
    iconBg: 'bg-gradient-to-br from-indigo-500 to-violet-600',
    route: '/mains',
    highlight: 'Get instant feedback on your answers!',
    color: 'indigo'
  },
  {
    id: 'daily-news',
    title: 'Daily News Digest',
    description: 'Curated daily news relevant to UPSC. Never miss important current affairs for your preparation.',
    icon: Calendar,
    iconBg: 'bg-gradient-to-br from-slate-600 to-slate-800',
    route: '/current-affairs',
    highlight: 'Start your day with UPSC-relevant news!',
    color: 'slate'
  },
  {
    id: 'explore-more',
    title: 'Explore More Features',
    description: 'This is just the beginning! CLARIAS has many more features designed to save your valuable preparation time and maximize your success.',
    icon: Sparkles,
    iconBg: 'bg-gradient-to-br from-fuchsia-500 to-pink-600',
    route: '/dashboard',
    highlight: 'Explore the app - we have much more to offer!',
    color: 'fuchsia'
  }
];

/**
 * GuidedTour - Step-by-step feature walkthrough for new users
 */
export const GuidedTour = ({ onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check if tour should be shown
  useEffect(() => {
    // Hard gate: never show until auth state resolved AND user is logged in
    if (authLoading || !user) {
      setIsVisible(false);
      return;
    }

    const hasCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
    const savedStep = localStorage.getItem(TOUR_STEP_KEY);
    
    // Don't show on landing, auth, or payment pages
    const excludedPaths = ['/landing', '/auth', '/login', '/pricing', '/payment', '/terms', '/privacy', '/refund'];
    const isExcluded = location.pathname === '/' || excludedPaths.some(path => location.pathname.startsWith(path));
    
    if (isExcluded || hasCompleted) {
      return;
    }
    
    // Resume from saved step if available
    if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }
    
    // Check periodically if the WhyChooseCLARIAS popup has been closed
    let intervalId = null;
    
    const checkAndShow = () => {
      const popupShown = sessionStorage.getItem('clarias-popup-shown');
      if (popupShown) {
        setIsVisible(true);
        // Clear interval once shown to prevent re-showing after close
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };
    
    // Initial check after popup would have shown
    const timer = setTimeout(checkAndShow, 3500);
    
    // Also check when popup might be dismissed
    intervalId = setInterval(checkAndShow, 500);
    
    return () => {
      clearTimeout(timer);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [location.pathname, user, authLoading]);

  // Save current step
  useEffect(() => {
    if (isVisible) {
      localStorage.setItem(TOUR_STEP_KEY, currentStep.toString());
    }
  }, [currentStep, isVisible]);

  const handleNext = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    } else {
      handleComplete();
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  }, [currentStep]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
    setIsVisible(false);
    onComplete?.();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
    setIsVisible(false);
    onComplete?.();
  }, [onComplete]);

  const handleTryFeature = useCallback(() => {
    const step = tourSteps[currentStep];
    navigate(step.route);
    // Auto-advance after navigation
    if (currentStep < tourSteps.length - 1) {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 500);
    } else {
      handleComplete();
    }
  }, [currentStep, navigate, handleComplete]);

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Tour Card */}
      <Card className={`relative w-full max-w-lg bg-white shadow-2xl border-0 overflow-hidden transition-all duration-300 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
        {/* Progress Bar */}
        <div className="h-1 bg-slate-100">
          <div 
            className={`h-full bg-gradient-to-r from-${step.color}-500 to-${step.color}-600 transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Header */}
        <div className={`${step.iconBg} p-6 text-white relative`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSkip}
            className="absolute top-3 right-3 text-white/80 hover:text-white hover:bg-white/20 h-8 w-8"
            data-testid="tour-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <Badge className="bg-white/20 text-white text-xs mb-2 font-normal">
                Step {currentStep + 1} of {tourSteps.length}
              </Badge>
              <h3 className="text-xl font-bold">{step.title}</h3>
            </div>
          </div>
        </div>
        
        <CardContent className="p-6 space-y-5">
          <p className="text-slate-600 leading-relaxed text-[15px]">
            {step.description}
          </p>
          
          {/* Highlight Box */}
          <div className={`bg-${step.color}-50 border border-${step.color}-200 rounded-xl p-4 flex items-start gap-3`}>
            <Sparkles className={`w-5 h-5 text-${step.color}-500 mt-0.5 flex-shrink-0`} />
            <p className={`text-sm text-${step.color}-700 font-medium`}>
              {step.highlight}
            </p>
          </div>
          
          {/* Progress Dots */}
          <div className="flex justify-center gap-1.5 pt-1">
            {tourSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? `bg-${step.color}-500 w-6` 
                    : idx < currentStep 
                      ? 'bg-slate-400 w-2' 
                      : 'bg-slate-200 w-2'
                }`}
              />
            ))}
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="text-slate-500 hover:text-slate-700"
              data-testid="tour-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTryFeature}
              className={`border-${step.color}-300 text-${step.color}-600 hover:bg-${step.color}-50`}
              data-testid="tour-try-btn"
            >
              Try Now
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            
            <Button
              onClick={handleNext}
              className={`${step.iconBg} text-white hover:opacity-90`}
              data-testid="tour-next-btn"
            >
              {currentStep === tourSteps.length - 1 ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Finish
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          {/* Skip Link */}
          <div className="text-center">
            <button
              onClick={handleSkip}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="tour-skip-btn"
            >
              Skip tour, I'll explore on my own
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * TourTriggerButton - Button to restart the tour
 */
export const TourTriggerButton = ({ className = '' }) => {
  const handleStartTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem(TOUR_STEP_KEY);
    window.location.reload();
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleStartTour}
      className={`gap-2 ${className}`}
      data-testid="restart-tour-btn"
    >
      <Sparkles className="w-4 h-4" />
      Take Tour
    </Button>
  );
};

/**
 * resetGuidedTour - Utility to reset tour state (for testing)
 */
export const resetGuidedTour = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  localStorage.removeItem(TOUR_STEP_KEY);
};

export default GuidedTour;
